import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  LLM_PROVIDERS,
  STT_PROVIDERS,
  TTS_PROVIDERS,
  VOICE_GENDERS,
  VOICE_LOCALE_KEYS,
} from 'components/@admin/@ai/aiConfigConstants';
import Loading from 'components/common/Loading';
import React, { useEffect, useMemo, useState } from 'react';
import { I18n } from 'utils/i18n';
import {
  useGetPlatformAiConfigQuery,
  usePatchPlatformAiConfigMutation,
} from 'store/app/entities/AiConfigSlice';

const TAB_CATALOG = 'catalog';
const TAB_DEFAULTS = 'defaults';
const TAB_VOICES = 'voices';

function ProviderStatus({ availability }) {
  if (!availability) return null;
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600 }}>
        {I18n.t('AdminAiConfigScene.ProviderStatus')}
      </Typography>
      <Stack spacing={1}>
        {['llm', 'stt', 'tts'].map(group => (
          <Box key={group} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            <Typography variant="body2" sx={{ minWidth: 48, fontWeight: 600 }}>
              {group.toUpperCase()}
            </Typography>
            {Object.entries(availability[group] || {}).map(([name, ok]) => (
              <Chip
                key={`${group}-${name}`}
                size="small"
                label={name}
                color={ok ? 'success' : 'default'}
                variant={ok ? 'filled' : 'outlined'}
              />
            ))}
          </Box>
        ))}
      </Stack>
    </Paper>
  );
}

const AdminAiConfigScene = () => {
  const { data, isLoading, isError, refetch } = useGetPlatformAiConfigQuery();
  const [patchPlatform, { isLoading: saving }] = usePatchPlatformAiConfigMutation();
  const [tab, setTab] = useState(TAB_DEFAULTS);
  const [draft, setDraft] = useState(null);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    if (data?.platform) {
      setDraft({
        models: { ...(data.platform.models || {}) },
        defaults: { ...(data.platform.defaults || {}) },
        voices: {
          cartesia: { ...(data.platform.voices?.cartesia || {}) },
          elevenlabs: { ...(data.platform.voices?.elevenlabs || {}) },
        },
      });
    }
  }, [data?.platform]);

  const modelKeys = useMemo(() => Object.keys(draft?.models || {}), [draft?.models]);

  const modelsForProvider = useMemo(() => {
    const provider = draft?.defaults?.llmProvider;
    if (!provider) return modelKeys;
    return modelKeys.filter(key => draft.models[key]?.provider === provider);
  }, [draft?.defaults?.llmProvider, draft?.models, modelKeys]);

  const providerAvailability = data?.providerAvailability;

  const isProviderAvailable = (group, name) => Boolean(providerAvailability?.[group]?.[name]);

  const handleSave = async () => {
    if (!draft) return;
    setFeedback(null);
    try {
      await patchPlatform(draft).unwrap();
      setFeedback({ severity: 'success', message: I18n.t('AdminAiConfigScene.Saved') });
    } catch {
      setFeedback({ severity: 'error', message: I18n.t('AdminAiConfigScene.SaveError') });
    }
  };

  const patchModel = (key, field, value) => {
    setDraft(prev => ({
      ...prev,
      models: {
        ...prev.models,
        [key]: { ...prev.models[key], [field]: value },
      },
    }));
  };

  const patchDefault = (field, value) => {
    setDraft(prev => ({
      ...prev,
      defaults: { ...prev.defaults, [field]: value },
    }));
  };

  const patchVoice = (provider, localeKey, gender, value) => {
    setDraft(prev => ({
      ...prev,
      voices: {
        ...prev.voices,
        [provider]: {
          ...prev.voices[provider],
          [localeKey]: {
            ...(prev.voices[provider]?.[localeKey] || {}),
            [gender]: value,
          },
        },
      },
    }));
  };

  const addModelKey = () => {
    const key = `custom_${Date.now()}`;
    setDraft(prev => ({
      ...prev,
      models: {
        ...prev.models,
        [key]: { label: '', provider: 'bedrock', modelId: '' },
      },
    }));
  };

  if (isLoading || !draft) return <Loading />;
  if (isError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="error"
          action={<Button onClick={refetch}>{I18n.t('AdminAiConfigScene.Retry')}</Button>}
        >
          {I18n.t('AdminAiConfigScene.LoadError')}
        </Alert>
      </Box>
    );
  }

  const ttsProvider = draft.defaults.ttsProvider || 'elevenlabs';

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="h5">{I18n.t('AdminAiConfigScene.Title')}</Typography>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {I18n.t('CompanyForm.Save')}
        </Button>
      </Stack>

      {feedback && (
        <Alert severity={feedback.severity} sx={{ mb: 2 }} onClose={() => setFeedback(null)}>
          {feedback.message}
        </Alert>
      )}

      <ProviderStatus availability={providerAvailability} />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mt: 3, mb: 2 }}>
        <Tab value={TAB_DEFAULTS} label={I18n.t('AdminAiConfigScene.TabDefaults')} />
        <Tab value={TAB_CATALOG} label={I18n.t('AdminAiConfigScene.TabCatalog')} />
        <Tab value={TAB_VOICES} label={I18n.t('AdminAiConfigScene.TabVoices')} />
      </Tabs>

      {tab === TAB_DEFAULTS && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack
            spacing={2}
            sx={{
              maxWidth: 480,
            }}
          >
            <FormControl fullWidth>
              <InputLabel>{I18n.t('AdminAiConfigScene.LlmProvider')}</InputLabel>
              <Select
                label={I18n.t('AdminAiConfigScene.LlmProvider')}
                value={draft.defaults.llmProvider || 'bedrock'}
                onChange={e => patchDefault('llmProvider', e.target.value)}
              >
                {LLM_PROVIDERS.map(p => (
                  <MenuItem key={p} value={p} disabled={!isProviderAvailable('llm', p)}>
                    {p}
                    {!isProviderAvailable('llm', p)
                      ? ` (${I18n.t('AdminAiConfigScene.NotConfigured')})`
                      : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>{I18n.t('AdminAiConfigScene.DefaultModel')}</InputLabel>
              <Select
                label={I18n.t('AdminAiConfigScene.DefaultModel')}
                value={draft.defaults.defaultModelKey || 'haiku'}
                onChange={e => patchDefault('defaultModelKey', e.target.value)}
              >
                {modelsForProvider.map(key => (
                  <MenuItem key={key} value={key}>
                    {draft.models[key]?.label || key}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>{I18n.t('AdminAiConfigScene.SttProvider')}</InputLabel>
              <Select
                label={I18n.t('AdminAiConfigScene.SttProvider')}
                value={draft.defaults.sttProvider || 'deepgram'}
                onChange={e => patchDefault('sttProvider', e.target.value)}
              >
                {STT_PROVIDERS.map(p => (
                  <MenuItem key={p} value={p} disabled={!isProviderAvailable('stt', p)}>
                    {p}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>{I18n.t('AdminAiConfigScene.TtsProvider')}</InputLabel>
              <Select
                label={I18n.t('AdminAiConfigScene.TtsProvider')}
                value={draft.defaults.ttsProvider || 'elevenlabs'}
                onChange={e => patchDefault('ttsProvider', e.target.value)}
              >
                {TTS_PROVIDERS.map(p => (
                  <MenuItem key={p} value={p} disabled={!isProviderAvailable('tts', p)}>
                    {p}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Paper>
      )}

      {tab === TAB_CATALOG && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack
            direction="row"
            sx={{
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
              }}
            >
              {I18n.t('AdminAiConfigScene.ModelCatalog')}
            </Typography>
            <Button size="small" onClick={addModelKey}>
              {I18n.t('AdminAiConfigScene.AddModel')}
            </Button>
          </Stack>
          <Stack spacing={2}>
            {modelKeys.map(key => (
              <Box key={key}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    mb: 0.5,
                    display: 'block',
                  }}
                >
                  {key}
                </Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                  <TextField
                    size="small"
                    label={I18n.t('AdminAiConfigScene.ModelLabel')}
                    value={draft.models[key]?.label || ''}
                    onChange={e => patchModel(key, 'label', e.target.value)}
                    sx={{ flex: 1 }}
                  />
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>{I18n.t('AdminAiConfigScene.Provider')}</InputLabel>
                    <Select
                      label={I18n.t('AdminAiConfigScene.Provider')}
                      value={draft.models[key]?.provider || 'bedrock'}
                      onChange={e => patchModel(key, 'provider', e.target.value)}
                    >
                      {LLM_PROVIDERS.map(p => (
                        <MenuItem key={p} value={p}>
                          {p}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    label={I18n.t('AdminAiConfigScene.ModelId')}
                    value={draft.models[key]?.modelId || ''}
                    onChange={e => patchModel(key, 'modelId', e.target.value)}
                    sx={{ flex: 2 }}
                  />
                </Stack>
                <Divider sx={{ mt: 2 }} />
              </Box>
            ))}
          </Stack>
        </Paper>
      )}

      {tab === TAB_VOICES && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              mb: 2,
            }}
          >
            {I18n.t('AdminAiConfigScene.VoiceMatrixHint', { provider: ttsProvider })}
          </Typography>
          <Stack spacing={2}>
            {VOICE_LOCALE_KEYS.map(localeKey => (
              <Box key={localeKey}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {localeKey}
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  {VOICE_GENDERS.map(gender => (
                    <TextField
                      key={gender}
                      size="small"
                      label={I18n.t(`AdminAiConfigScene.Gender.${gender}`)}
                      value={draft.voices[ttsProvider]?.[localeKey]?.[gender] || ''}
                      onChange={e => patchVoice(ttsProvider, localeKey, gender, e.target.value)}
                      sx={{ flex: 1 }}
                    />
                  ))}
                </Stack>
                <Divider sx={{ mt: 2 }} />
              </Box>
            ))}
          </Stack>
        </Paper>
      )}
    </Box>
  );
};

export default AdminAiConfigScene;
